import {Card, Choice, FormField, Separator, Switch, type FormControl} from 'platform/components';
import {Box, Show, Grid, GridItem, Heading, HStack, Space, Text, VStack} from 'platform/foundation';

import {useWatch, type UseFormReturn} from 'react-hook-form';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';
import {useBranches, useCurrencies} from '@dms/shared';

import type {CheckoutProfileFormValues} from '../types/CheckoutProfileFormValuesSchema';

interface Props {
  control: FormControl<CheckoutProfileFormValues>;
  isCreating: boolean;
  formApi: UseFormReturn<CheckoutProfileFormValues>;
  currency?: string;
  fieldErrors?: Record<string, string>;
}

export function CheckoutProfileGeneral(props: Props) {
  const {branchOptions, isLoading} = useBranches();
  const {currencyOptions} = useCurrencies();
  const typeOfSale = useWatch({control: props.control, name: 'typeOfSale'});
  const foreignAllowed = useWatch({control: props.control, name: 'foreignCurrency.isAllowed'});
  const foreignDefault = useWatch({control: props.control, name: 'foreignCurrency.isDefault'});
  const allowDepositsLabel = i18n.t('entity.checkout.labels.allowDeposits');
  const allowForeignCurrencyLabel = i18n.t('entity.checkout.labels.allowForeignCurrencyPayment');
  const domesticLabel = i18n.t('entity.checkout.labels.domestic');
  const saleToEuropeLabel = i18n.t('entity.checkout.labels.abroadSaleEU');
  const saleOutsideEuropeLabel = i18n.t('entity.checkout.labels.abroadSaleOutsideEU');

  return (
    <VStack>
      <Grid columns={4} spacing={4} align="flex-start">
        <GridItem span={2}>
          <FormField
            control={props.control}
            name="name"
            errorMessage={props.fieldErrors?.name}
            type="text"
            label={i18n.t('entity.checkout.labels.profileName')}
            isRequired
            maxLength={100}
          />
        </GridItem>
        <GridItem>
          <Choice
            label={i18n.t('page.settings.labels.primaryCurrency')}
            value={props.currency ?? null}
            options={currencyOptions}
            isRequired
            isDisabled
            isNotClearable
          />
        </GridItem>
        <GridItem>
          <Space vertical={6} />
          <Box id="checkout-focus-isActive" tabIndex={-1}>
            <FormField
              control={props.control}
              name="isActive"
              type="switch"
              label={i18n.t('general.labels.active')}
            />
          </Box>
        </GridItem>
        <GridItem span={2}>
          <FormField
            control={props.control}
            onChange={() => {
              const methods = props.formApi.getValues('payments.methods');

              if (isNotNil(methods)) {
                props.formApi.setValue(
                  'payments.methods',
                  methods.map((payment) => {
                    if (payment.method === 'CASH') {
                      return {...payment, cashRegisterId: undefined};
                    }

                    if (payment.method === 'BANK_TRANSFER') {
                      return {...payment, bankAccount: undefined};
                    }

                    return payment;
                  }),
                  {shouldDirty: true}
                );
              }
            }}
            name="branchId"
            errorMessage={props.fieldErrors?.branchId}
            type="choice"
            label={i18n.t('entity.branch.labels.branch')}
            options={branchOptions}
            isLoading={isLoading}
            isDisabled={!props.isCreating}
            isNotClearable
            isRequired
          />
        </GridItem>
        <GridItem span={2}>
          <FormField
            control={props.control}
            name="typeOfSale"
            errorMessage={props.fieldErrors?.typeOfSale}
            type="choice"
            label={i18n.t('entity.checkout.labels.typeOfSale')}
            isRequired
            options={[
              {value: 'DEFAULT', label: i18n.t('entity.checkout.labels.external')},
              {value: 'INTERNAL', label: i18n.t('entity.checkout.labels.internal')},
            ]}
            isDisabled={!props.isCreating}
            isNotClearable
          />
        </GridItem>
      </Grid>
      <Show when={typeOfSale === 'DEFAULT'}>
        <>
          <Separator />
          <VStack>
            <Heading size={4}>{i18n.t('entity.checkout.labels.saleRegimes')}</Heading>
            <Space vertical={1} />
            <Text size="small" color="secondary">
              {i18n.t('entity.checkout.labels.saleRegimesDescription')}
            </Text>
            <Space vertical={4} />
            <Card variant="inlineGrey">
              <Grid columns={4} spacing={4} align="center">
                <GridItem>
                  <HStack align="center" spacing={3}>
                    <Switch value isDisabled aria-label={domesticLabel} />
                    <Heading size={4}>{domesticLabel}</Heading>
                  </HStack>
                </GridItem>
                <GridItem>
                  <HStack align="center" spacing={3}>
                    <Box id="checkout-focus-saleRegimes.inEurope" tabIndex={-1}>
                      <FormField
                        control={props.control}
                        name="saleRegimes.inEurope"
                        errorMessage={props.fieldErrors?.['saleRegimes.inEurope']}
                        type="switch"
                        aria-label={saleToEuropeLabel}
                      />
                    </Box>
                    <Heading size={4}>{saleToEuropeLabel}</Heading>
                  </HStack>
                </GridItem>
                <GridItem>
                  <HStack align="center" spacing={3}>
                    <Box id="checkout-focus-saleRegimes.outOfEurope" tabIndex={-1}>
                      <FormField
                        control={props.control}
                        name="saleRegimes.outOfEurope"
                        errorMessage={props.fieldErrors?.['saleRegimes.outOfEurope']}
                        type="switch"
                        aria-label={saleOutsideEuropeLabel}
                      />
                    </Box>
                    <Heading size={4}>{saleOutsideEuropeLabel}</Heading>
                  </HStack>
                </GridItem>
              </Grid>
            </Card>
          </VStack>
          <Separator />
          <VStack>
            <Heading size={4}>{i18n.t('entity.checkout.labels.deposits')}</Heading>
            <Space vertical={1} />
            <Text size="small" color="secondary">
              {i18n.t('entity.checkout.labels.depositsDescription')}
            </Text>
            <Space vertical={4} />
            <Card variant="inlineGrey">
              <HStack align="center" spacing={3}>
                <Box id="checkout-focus-deposit.isAllowed" tabIndex={-1}>
                  <FormField
                    control={props.control}
                    name="deposit.isAllowed"
                    errorMessage={props.fieldErrors?.['deposit.isAllowed']}
                    type="switch"
                    aria-label={allowDepositsLabel}
                  />
                </Box>
                <Heading size={4}>{allowDepositsLabel}</Heading>
              </HStack>
            </Card>
          </VStack>
          <Separator />
          <VStack>
            <Heading size={4}>{i18n.t('entity.checkout.labels.foreignCurrencyPayment')}</Heading>
            <Space vertical={1} />
            <Text size="small" color="secondary">
              {i18n.t('entity.checkout.labels.foreignCurrencyPaymentDescription')}
            </Text>
            <Space vertical={4} />
            <Card variant="inlineGrey">
              <HStack align="center" spacing={3}>
                <Box id="checkout-focus-foreignCurrency.isAllowed" tabIndex={-1}>
                  <FormField
                    control={props.control}
                    name="foreignCurrency.isAllowed"
                    errorMessage={props.fieldErrors?.['foreignCurrency.isAllowed']}
                    type="switch"
                    aria-label={allowForeignCurrencyLabel}
                    onChange={(enabled) => {
                      if (!enabled) {
                        props.formApi.setValue('foreignCurrency.isDefault', false, {
                          shouldDirty: true,
                        });

                        props.formApi.setValue('foreignCurrency.defaultCurrency', null, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                  />
                </Box>
                <Heading size={4}>{allowForeignCurrencyLabel}</Heading>
              </HStack>
              <Show when={foreignAllowed}>
                <>
                  <Separator />
                  <Grid columns={4} spacing={4} align="flex-start">
                    <GridItem>
                      <Space vertical={6} />
                      <Box id="checkout-focus-foreignCurrency.isDefault" tabIndex={-1}>
                        <FormField
                          control={props.control}
                          name="foreignCurrency.isDefault"
                          errorMessage={props.fieldErrors?.['foreignCurrency.isDefault']}
                          type="switch"
                          label={i18n.t('entity.checkout.labels.defaultForeignCurrencyPayment')}
                          onChange={(enabled) => {
                            if (!enabled) {
                              props.formApi.setValue('foreignCurrency.defaultCurrency', null, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }
                          }}
                        />
                      </Box>
                    </GridItem>
                    <GridItem>
                      <FormField
                        control={props.control}
                        name="foreignCurrency.defaultCurrency"
                        errorMessage={props.fieldErrors?.['foreignCurrency.defaultCurrency']}
                        type="choice"
                        label={i18n.t('entity.checkout.labels.defaultPaymentCurrency')}
                        options={currencyOptions}
                        isDisabled={foreignDefault !== true}
                        isRequired={foreignDefault === true}
                      />
                    </GridItem>
                  </Grid>
                </>
              </Show>
            </Card>
          </VStack>
        </>
      </Show>
    </VStack>
  );
}
